import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CouverturesService } from './couvertures.service';

export interface PhotoGalerie {
  id: number;
  nom?: string;
  miniatureUrl?: string;
  affichageUrl?: string;
  originalUrl?: string;
  largeur?: number;
  hauteur?: number;
  datePrise?: string;
  /** PRISE (date de prise de vue) | ENVOI (date d'envoi, faute de mieux) */
  datePriseSource?: 'PRISE' | 'ENVOI';
  dateEnvoi?: string;
  auteur?: { uid?: string; nom?: string } | null;
  lien?: { type?: string; id?: number; label?: string; module?: string } | null;
  legende?: string;
  peutSupprimer?: boolean;
}

export interface PageGalerie { items: PhotoGalerie[]; curseurSuivant: string | null; total?: number; }

export interface FiltresGalerieDispo {
  modules: { code: string; label: string; nombre: number }[];
  elements: { lienType: string; lienId: number; label: string; module?: string; nombre: number }[];
  auteurs: { id: string; nom: string; nombre: number }[];
  plusAncienne?: string;
  plusRecente?: string;
  total?: number;
}

export interface FiltresGalerie {
  module?: string; lienType?: string; lienId?: number | string; auteur?: string; du?: string; au?: string;
}

/**
 * Galerie Wanar : photos de la ferme, droits filtrés par le serveur. Images authentifiées lues en blob
 * (cache HTTP immuable + ETag côté serveur) ; adresses blob: gardées pour la session, les plus anciennes
 * images d'affichage libérées au-delà de 40.
 */
@Injectable({ providedIn: 'root' })
export class GalerieService {
  private readonly base = `${environment.BASE_URL}/api/galerie`;
  private readonly docs = `${environment.BASE_URL}/api/documents`;
  private readonly affichages = new Map<number, { obs: Observable<string | null>; url?: string }>();

  constructor(private http: HttpClient, private couvertures: CouverturesService) {}

  page(fermeId: number, filtres: FiltresGalerie, curseur?: string | null, limite = 60): Observable<PageGalerie> {
    return this.http.get<PageGalerie>(this.base, { params: this.params({ fermeId, ...filtres, curseur, limite }) });
  }

  filtres(fermeId: number): Observable<FiltresGalerieDispo> {
    return this.http.get<FiltresGalerieDispo>(`${this.base}/filtres`, { params: { fermeId } });
  }

  supprimer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.docs}/${id}`);
  }

  /** Miniature, cache partagé avec les cartes */
  miniature(id: number): Observable<string | null> { return this.couvertures.vignette(id); }

  /** Image d'affichage (1 600 px, orientation corrigée) ; jamais l'original */
  affichage(id: number): Observable<string | null> {
    let e = this.affichages.get(id);
    // Ordre d'usage : une image revue passe en dernier (libérée en dernier)
    if (e) { this.affichages.delete(id); this.affichages.set(id, e); }
    if (!e) {
      const entree: { obs: Observable<string | null>; url?: string } = { obs: of(null) };
      entree.obs = this.http.get(`${this.docs}/${id}/affichage`, { responseType: 'blob' }).pipe(
        map(b => (entree.url = URL.createObjectURL(b))),
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.affichages.set(id, entree);
      e = entree;
      this.liberer();
    }
    return e.obs;
  }

  /** Précharge l'image d'affichage (photos voisines de la visionneuse) */
  precharger(id: number): void { this.affichage(id).subscribe(); }

  /** Image d'affichage en fichier (IA, facture, pièce) : la photo d'origine reste dans la Galerie */
  fichierAffichage(p: PhotoGalerie): Observable<File> {
    return this.http.get(`${this.docs}/${p.id}/affichage`, { responseType: 'blob' }).pipe(
      map(b => new File([b], (p.nom || `photo-${p.id}`).replace(/\.[^.]+$/, '') + '.jpg',
        { type: b.type || 'image/jpeg', lastModified: p.datePrise ? new Date(p.datePrise).getTime() : Date.now() }))
    );
  }

  /** Original, uniquement à la demande */
  telechargerOriginal(p: PhotoGalerie): void {
    this.http.get(`${this.docs}/${p.id}/fichier`, { params: { telecharger: 'true' }, responseType: 'blob' }).subscribe(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = p.nom || `photo-${p.id}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    });
  }

  private liberer(): void {
    while (this.affichages.size > 40) {
      const [id, e] = this.affichages.entries().next().value as [number, { url?: string }];
      if (e.url) { URL.revokeObjectURL(e.url); }
      this.affichages.delete(id);
    }
  }

  private params(o: any): any {
    const p: any = {};
    for (const [k, v] of Object.entries(o)) { if (v !== undefined && v !== null && v !== '') { p[k] = String(v); } }
    return p;
  }
}
